import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional fallback render. Defaults to a simple inline panel. */
  fallback?: (error: Error) => ReactNode;
  /** Optional handler for telemetry / logging. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Class-component error boundary. React still requires a class for this.
 * Used at page-level wrappers so a buggy panel never wipes the whole UI.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error);
      return (
        <div role="alert" className="rounded-sm border border-vermilion/40 bg-vermilion/5 p-6">
          <p className="smallcaps text-vermilion">Something went wrong</p>
          <p className="mt-2 text-body text-ink">{this.state.error.message}</p>
          <button
            type="button"
            onClick={this.reset}
            className="mt-4 rounded-sm border border-ink/20 px-3 py-1 font-mono text-[11px] text-ink hover:bg-ink/5"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
